import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import moment from "moment";

export default function AIInsightsPanel() {
  const { user, loading, token } = useContext(AuthContext);

  const [masterSites, setMasterSites] = useState([]);
  const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Raw data
  const [today, setToday] = useState([]); // from /rep/today/master/:id
  const [daily30, setDaily30] = useState([]); // from /reports/daily/master/:id?from&to
  const [dailyFc, setDailyFc] = useState({ history: [], forecast: [] });
  const [monthlyFc, setMonthlyFc] = useState({ history: [], forecast: [] });

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  /* -------------------------- helpers -------------------------- */
  const toNum = (v) =>
    Number.parseFloat(
      (v && v.$numberDecimal) ? v.$numberDecimal : (v ?? 0)
    ) || 0;

  const groupBy = (arr, key) =>
    arr.reduce((m, x) => {
      const k = typeof key === "function" ? key(x) : x[key];
      if (!m[k]) m[k] = [];
      m[k].push(x);
      return m;
    }, {});

  const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const std = (arr) => {
    if (arr.length < 2) return 0;
    const m = mean(arr);
    const v = mean(arr.map((x) => (x - m) * (x - m)));
    return Math.sqrt(v);
  };
  const median = (arr) => {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  };

  // quality ranges (can tune)
  const SAFE_TDS_MIN = 50, SAFE_TDS_MAX = 150;
  const SAFE_PH_MIN = 6.5, SAFE_PH_MAX = 8.5;

  /* ------------------------ data fetch ------------------------- */
  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/mastersites", authHeader);
      const filtered =
        user?.role === "admin"
          ? res.data
          : res.data.filter((ms) => (user?.masterSiteAccess || []).includes(ms._id));
      setMasterSites(filtered);
      if (filtered.length) setSelectedMasterSiteId(filtered[0]._id);
    } catch (e) {
      console.error(e);
      setErr("Failed to load master sites");
    }
  };

  const fetchAll = async (id) => {
    setErr("");
    setBusy(true);
    try {
      const todayRes = await axios.get(`/rep/today/master/${id}`, authHeader);
      setToday(Array.isArray(todayRes.data) ? todayRes.data : []);

      // last 30 days daily (DD/MM/YYYY)
      const end = moment().format("DD/MM/YYYY");
      const start = moment().subtract(30, "days").format("DD/MM/YYYY");
      const d30 = await axios.get(
        `/reports/daily/master/${id}?from=${start}&to=${end}`,
        authHeader
      );
      setDaily30(Array.isArray(d30.data) ? d30.data : []);

      // forecasts
      const dFc = await axios.get(`/forecast/daily/master/${id}?h=7`, authHeader);
      setDailyFc(dFc.data || { history: [], forecast: [] });

      const mFc = await axios.get(`/forecast/monthly/master/${id}?months=1`, authHeader);
      setMonthlyFc(mFc.data || { history: [], forecast: [] });
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load insights");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
      if (user) {
        fetchMasterSites();
      }
    }, [user]);

  useEffect(() => {
    if (selectedMasterSiteId) fetchAll(selectedMasterSiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMasterSiteId]);

  /* ------------------------ AI Insights ------------------------ */

  // 1) Consumption anomalies (z-score vs last 7 days per site)
  const anomalies = useMemo(() => {
    // build per-site past values (last 7 days)
    const bySite = groupBy(daily30, (r) => r.siteId?.toString?.() || r.siteId || r.site);
    const todayBySite = new Map(
      today.map((t) => [t.siteId?.toString?.() || t.siteId || t.site, t])
    );
    const out = [];
    Object.entries(bySite).forEach(([key, arr]) => {
      // for each calendar date latest per site
      const last7Dates = new Set(
        Array.from({ length: 7 }, (_, i) => moment().subtract(i, "days").format("DD/MM/YYYY"))
      );
      const vals = arr
        .filter((x) => last7Dates.has(x.date))
        .map((x) => toNum(x.totalWaterDispensed));
      const m = mean(vals);
      const s = std(vals);
      const t = todayBySite.get(key);
      const todayVal = toNum(t?.totalWaterDispensed);
      if (vals.length >= 3) {
        const z = s > 0 ? (todayVal - m) / s : 0;
        if (Math.abs(z) >= 2) {
          out.push({
            site: t?.site || arr[0]?.site || "Unknown",
            zScore: Number(z.toFixed(2)),
            today: Number(todayVal.toFixed(2)),
            baselineMean: Number(m.toFixed(2)),
            direction: z > 0 ? "High spike" : "Unusual drop",
          });
        }
      }
    });
    return out.sort((a, b) => Math.abs(b.zScore) - Math.abs(a.zScore)).slice(0, 5);
  }, [daily30, today]);

  // 2) Quality risk (range violation + short trend)
  const qualityRisks = useMemo(() => {
    const todayBySite = new Map(
      today.map((t) => [t.siteId?.toString?.() || t.siteId || t.site, t])
    );
    const bySite = groupBy(daily30, (r) => r.siteId?.toString?.() || r.siteId || r.site);
    const risks = [];

    Object.entries(bySite).forEach(([key, arr]) => {
      const t = todayBySite.get(key);
      const tdsToday = toNum(t?.tds);
      const phToday = toNum(t?.ph);

      // median of last 7 days for trend-ish signal
      const last7Dates = new Set(
        Array.from({ length: 7 }, (_, i) => moment().subtract(i, "days").format("DD/MM/YYYY"))
      );
      const recent = arr.filter((x) => last7Dates.has(x.date));
      const tdsSeries = recent.map((x) => toNum(x.tds)).filter((n) => n > 0);
      const phSeries = recent.map((x) => toNum(x.ph)).filter((n) => n > 0);

      const tdsMed = median(tdsSeries);
      const phMed = median(phSeries);

      let score = 0;
      let notes = [];

      // range
      if (tdsToday && (tdsToday < SAFE_TDS_MIN || tdsToday > SAFE_TDS_MAX)) {
        score += 3;
        notes.push(`TDS ${tdsToday} out of ${SAFE_TDS_MIN}-${SAFE_TDS_MAX}`);
      }
      if (phToday && (phToday < SAFE_PH_MIN || phToday > SAFE_PH_MAX)) {
        score += 3;
        notes.push(`pH ${phToday} out of ${SAFE_PH_MIN}-${SAFE_PH_MAX}`);
      }

      // trend (today vs median 7d)
      if (tdsToday && tdsMed && Math.abs(tdsToday - tdsMed) > 20) {
        score += 1;
        notes.push(`TDS deviates ${Math.round(tdsToday - tdsMed)} vs 7d median`);
      }
      if (phToday && phMed && Math.abs(phToday - phMed) > 0.3) {
        score += 1;
        notes.push(`pH deviates ${Number((phToday - phMed).toFixed(2))} vs 7d median`);
      }

      if (score >= 3) {
        risks.push({
          site: t?.site || arr[0]?.site || "Unknown",
          score,
          tdsToday,
          phToday,
          notes,
        });
      }
    });

    return risks.sort((a, b) => b.score - a.score).slice(0, 5);
  }, [daily30, today]);

  // 3) Maintenance signals (zero/near-zero or OFF patterns)
  const maintenanceSignals = useMemo(() => {
    const todayBySite = new Map(
      today.map((t) => [t.siteId?.toString?.() || t.siteId || t.site, t])
    );
    const bySite = groupBy(daily30, (r) => r.siteId?.toString?.() || r.siteId || r.site);
    const out = [];

    Object.entries(bySite).forEach(([key, arr]) => {
      const t = todayBySite.get(key);
      const siteName = t?.site || arr[0]?.site || "Unknown";

      // last 3 days totals including today (using daily docs, not REGULAR deltas)
      const last3Dates = new Set(
        Array.from({ length: 3 }, (_, i) => moment().subtract(i, "days").format("DD/MM/YYYY"))
      );
      const last3 = arr
        .filter((x) => last3Dates.has(x.date))
        .map((x) => toNum(x.totalWaterDispensed));

      const zeros = last3.filter((v) => v <= 1e-6).length;
      const todayDelta = toNum(t?.totalWaterDispensed);

      if (zeros >= 2 || todayDelta <= 1e-6) {
        out.push({
          site: siteName,
          recentZeros: zeros,
          todayDelta: Number(todayDelta.toFixed(2)),
          hint:
            zeros >= 2
              ? "No dispensing in 2 of last 3 days"
              : "Zero dispensing today",
        });
      }
    });

    return out.slice(0, 5);
  }, [daily30, today]);

  // 4) Quick suggestions (rule-based for now)
  const suggestions = useMemo(() => {
    const list = [];

    if (anomalies.length) {
      const top = anomalies[0];
      list.push(
        `Investigate ${top.site}: ${top.direction} (z=${top.zScore}). Compare with past week usage pattern.`
      );
    }
    if (qualityRisks.length) {
      const q = qualityRisks[0];
      list.push(
        `Quality alert at ${q.site}: ${q.notes.join("; ")}. Consider on-site test and cartridge check.`
      );
    }
    if (maintenanceSignals.length) {
      const m = maintenanceSignals[0];
      list.push(
        `Maintenance check for ${m.site}: ${m.hint}. Verify power/pump/filter status.`
      );
    }

    // forecast-level suggestion
    const next7Total = dailyFc.forecast.reduce((a, b) => a + (b?.value || 0), 0);
    if (next7Total > 0) {
      list.push(
        `Plan tanker/refill schedules: projected next 7 days total ≈ ${Math.round(
          next7Total
        )} L across master site.`
      );
    }
    return list.slice(0, 5);
  }, [anomalies, qualityRisks, maintenanceSignals, dailyFc]);

  /* -------------------------- UI bits -------------------------- */
  const Badge = ({ type, children }) => {
    const map = {
      danger: "#DC2626",
      warn: "#D97706",
      info: "#2563EB",
      ok: "#16A34A",
    };
    const bg = {
      danger: "#FEE2E2",
      warn: "#FEF3C7",
      info: "#DBEAFE",
      ok: "#DCFCE7",
    };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          color: map[type] || "#374151",
          background: bg[type] || "#E5E7EB",
        }}
      >
        {children}
      </span>
    );
  };

  if (loading) return <p>Loading user...</p>;
  if (!user) return <p>🔒 Not logged in</p>;

  return (
    <div className="report-viewer-container">
      <div className="flex justify-between items-center mb-4">
        <h2 className="report-heading">AI Insights — Master Site</h2>
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium">Select Master Site:</label>
        <select
          className="border rounded px-2 py-1 w-full"
          value={selectedMasterSiteId}
          onChange={(e) => setSelectedMasterSiteId(e.target.value)}
        >
          {masterSites.map((ms) => (
            <option key={ms._id} value={ms._id}>
              {ms.name}
            </option>
          ))}
        </select>
      </div>

      {busy && <p>Crunching insights…</p>}
      {err && <p className="text-red-500">{err}</p>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginTop: 12,
        }}
      >
        {/* Consumption Anomalies */}
        <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Consumption Anomalies</h3>
            <Badge type="info">last 7d vs today</Badge>
          </div>
          {anomalies.length === 0 ? (
            <p className="text-gray-500">No significant anomalies.</p>
          ) : (
            <table className="report-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-1">Site</th>
                  <th className="border px-2 py-1">Today (L)</th>
                  <th className="border px-2 py-1">Baseline (L)</th>
                  <th className="border px-2 py-1">z</th>
                  <th className="border px-2 py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {anomalies.map((a, i) => (
                  <tr key={i}>
                    <td className="border px-2 py-1">{a.site}</td>
                    <td className="border px-2 py-1">{a.today}</td>
                    <td className="border px-2 py-1">{a.baselineMean}</td>
                    <td className="border px-2 py-1">{a.zScore}</td>
                    <td className="border px-2 py-1">
                      <Badge type={a.zScore > 0 ? "warn" : "danger"}>{a.direction}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Quality Risks */}
        <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Quality Risks</h3>
            <Badge type="danger">TDS/pH watch</Badge>
          </div>
          {qualityRisks.length === 0 ? (
            <p className="text-gray-500">No quality risks detected.</p>
          ) : (
            <table className="report-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-1">Site</th>
                  <th className="border px-2 py-1">TDS</th>
                  <th className="border px-2 py-1">pH</th>
                  <th className="border px-2 py-1">Flags</th>
                  <th className="border px-2 py-1">Risk</th>
                </tr>
              </thead>
              <tbody>
                {qualityRisks.map((q, i) => (
                  <tr key={i}>
                    <td className="border px-2 py-1">{q.site}</td>
                    <td className="border px-2 py-1">{q.tdsToday || "-"}</td>
                    <td className="border px-2 py-1">{q.phToday || "-"}</td>
                    <td className="border px-2 py-1">{q.notes.join("; ")}</td>
                    <td className="border px-2 py-1">
                      <Badge type={q.score >= 5 ? "danger" : "warn"}>{q.score >= 5 ? "High" : "Moderate"}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Maintenance Signals */}
        <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Maintenance Signals</h3>
            <Badge type="warn">uptime</Badge>
          </div>
          {maintenanceSignals.length === 0 ? (
            <p className="text-gray-500">All good. No downtime patterns seen.</p>
          ) : (
            <table className="report-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-1">Site</th>
                  <th className="border px-2 py-1">Zeros (3d)</th>
                  <th className="border px-2 py-1">Today (L)</th>
                  <th className="border px-2 py-1">Hint</th>
                </tr>
              </thead>
              <tbody>
                {maintenanceSignals.map((m, i) => (
                  <tr key={i}>
                    <td className="border px-2 py-1">{m.site}</td>
                    <td className="border px-2 py-1">{m.recentZeros}</td>
                    <td className="border px-2 py-1">{m.todayDelta}</td>
                    <td className="border px-2 py-1">{m.hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Suggestions */}
        <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>AI Suggestions</h3>
            <Badge type="info">actionable</Badge>
          </div>
          {suggestions.length === 0 ? (
            <p className="text-gray-500">No specific actions required.</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {suggestions.map((s, i) => (
                <li key={i} style={{ marginBottom: 6 }}>{s}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Forecast summaries (compact, no charts to keep panel light) */}
      <div style={{ marginTop: 16, border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
          Forecast Summary
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>Next 7 days (Master total)</div>
            <div style={{ fontWeight: 700 }}>
              ≈ {Math.round(dailyFc.forecast.reduce((a, b) => a + (b?.value || 0), 0))} L
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>Next month (Master total)</div>
            <div style={{ fontWeight: 700 }}>
              {monthlyFc.forecast?.[0]?.month || "-"}: {Math.round(monthlyFc.forecast?.[0]?.value || 0)} L
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
